'use client'

import { useState, useEffect, forwardRef } from 'react'
import { Check, X, Info, CreditCard } from 'lucide-react'
import { isValidIBAN, electronicFormatIBAN, friendlyFormatIBAN, getCountrySpecifications } from 'ibantools'
import validator from 'validator'
import { cn } from '../utils/styles'

export interface IBANInputProps {
  value?: string
  onChange?: (value: string, isValid: boolean, metadata?: IBANMetadata) => void
  onBlur?: () => void
  error?: string
  required?: boolean
  disabled?: boolean
  showBIC?: boolean
  bicValue?: string
  onBICChange?: (value: string) => void
  placeholder?: string
  className?: string
  label?: string
  helperText?: string
  showTooltip?: boolean
}

export interface IBANMetadata {
  country: string
  countryName: string
  bankCode?: string
  isValid: boolean
  formatted: string
  length: number
}

const COUNTRY_NAMES: { [key: string]: string } = {
  CH: 'Switzerland', FR: 'France', DE: 'Germany', IT: 'Italy', ES: 'Spain',
  NL: 'Netherlands', BE: 'Belgium', AT: 'Austria', GB: 'United Kingdom',
  IE: 'Ireland', PT: 'Portugal', LU: 'Luxembourg', NO: 'Norway',
  SE: 'Sweden', DK: 'Denmark', FI: 'Finland', PL: 'Poland',
  CZ: 'Czech Republic', HU: 'Hungary', RO: 'Romania', GR: 'Greece',
  HR: 'Croatia', SI: 'Slovenia', SK: 'Slovakia', EE: 'Estonia',
  LV: 'Latvia', LT: 'Lithuania', BG: 'Bulgaria', CY: 'Cyprus',
  MT: 'Malta', IS: 'Iceland', LI: 'Liechtenstein', MC: 'Monaco',
  AD: 'Andorra', AL: 'Albania', AZ: 'Azerbaijan', BA: 'Bosnia and Herzegovina',
  BY: 'Belarus', CR: 'Costa Rica', DO: 'Dominican Republic', EG: 'Egypt',
  FO: 'Faroe Islands', GE: 'Georgia', GI: 'Gibraltar', GL: 'Greenland',
  GT: 'Guatemala', IL: 'Israel', JO: 'Jordan', KW: 'Kuwait',
  KZ: 'Kazakhstan', LB: 'Lebanon', LC: 'Saint Lucia', MD: 'Moldova',
  ME: 'Montenegro', MK: 'North Macedonia', MR: 'Mauritania', MU: 'Mauritius',
  PK: 'Pakistan', PS: 'Palestine', QA: 'Qatar', RS: 'Serbia',
  SA: 'Saudi Arabia', SM: 'San Marino', TN: 'Tunisia', TR: 'Turkey',
  UA: 'Ukraine', VA: 'Vatican City', VG: 'British Virgin Islands', XK: 'Kosovo',
}

export const IBANInput = forwardRef<HTMLInputElement, IBANInputProps>(({
  value = '',
  onChange,
  onBlur,
  error,
  required = false,
  disabled = false,
  showBIC = false,
  bicValue = '',
  onBICChange,
  placeholder = "CH93 0076 2011 6238 5295 7",
  className,
  label = "IBAN (International Bank Account Number)",
  helperText,
  showTooltip = true,
  ...props
}, ref) => {
  const [localValue, setLocalValue] = useState(value)
  const [isValid, setIsValid] = useState(false)
  const [metadata, setMetadata] = useState<IBANMetadata | null>(null)

  // Extract metadata from IBAN using ibantools
  const extractMetadata = (iban: string): IBANMetadata | null => {
    const cleaned = electronicFormatIBAN(iban) || ''
    if (cleaned.length < 2) return null

    const countryCode = cleaned.substring(0, 2)
    const valid = isValidIBAN(cleaned)
    const countrySpec = getCountrySpecifications()[countryCode]

    return {
      country: countryCode,
      countryName: COUNTRY_NAMES[countryCode] || 'Unknown',
      bankCode: countrySpec && cleaned.length >= countrySpec.bankCodeStart + countrySpec.bankCodeLength
        ? cleaned.substring(countrySpec.bankCodeStart, countrySpec.bankCodeStart + countrySpec.bankCodeLength)
        : undefined,
      isValid: valid,
      formatted: friendlyFormatIBAN(cleaned) || cleaned,
      length: cleaned.length,
    }
  }

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.toUpperCase()
    // Only allow alphanumeric and spaces
    const cleaned = input.replace(/[^A-Z0-9\s]/g, '')

    setLocalValue(cleaned)

    const electronicFormat = electronicFormatIBAN(cleaned) || ''
    if (electronicFormat.length >= 2) {
      const meta = extractMetadata(electronicFormat)
      setMetadata(meta)
      setIsValid(meta?.isValid || false)
      onChange?.(electronicFormat, meta?.isValid || false, meta || undefined)
    } else {
      setMetadata(null)
      setIsValid(false)
      onChange?.(electronicFormat, false)
    }
  }

  // Format on blur
  const handleBlur = () => {
    if (localValue) {
      const electronicFormat = electronicFormatIBAN(localValue) || ''
      const formatted = friendlyFormatIBAN(electronicFormat) || electronicFormat
      setLocalValue(formatted)
    }
    onBlur?.()
  }

  // Validate BIC/SWIFT code using validator
  const validateBIC = (bic: string): boolean => {
    return validator.isBIC(bic)
  }

  useEffect(() => {
    if (value && value !== electronicFormatIBAN(localValue)) {
      const formatted = friendlyFormatIBAN(value) || value
      setLocalValue(formatted)
      const meta = extractMetadata(value)
      setMetadata(meta)
      setIsValid(meta?.isValid || false)
    }
  }, [value, localValue])

  return (
    <div className={cn("space-y-4", className)}>
      {/* IBAN Input */}
      <div className="space-y-2">
        {label && (
          <label htmlFor="iban" className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            <CreditCard className="h-4 w-4" />
            {label}
            {required && <span className="text-red-500">*</span>}
            {showTooltip && (
              <div className="relative group">
                <Info className="h-3 w-3 cursor-help text-gray-500" />
                <div className="absolute left-0 top-6 z-10 hidden group-hover:block w-64 p-2 text-xs text-white bg-black rounded shadow-lg">
                  Your IBAN is used for receiving payments. It's encrypted and stored securely.
                </div>
              </div>
            )}
          </label>
        )}

        <div className="relative">
          <input
            ref={ref}
            id="iban"
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              'pr-10 font-mono',
              error && 'border-red-500',
              isValid && 'border-green-500',
            )}
            maxLength={35}
            autoComplete="off"
            spellCheck="false"
            {...props}
          />

          {localValue && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isValid ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-red-600" />
              )}
            </div>
          )}
        </div>

        {/* Metadata Display */}
        {metadata && (
          <div className="flex items-center gap-2">
            <span className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              isValid
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
            )}>
              {metadata.countryName}
            </span>
            {isValid && (
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                Valid IBAN
              </span>
            )}
            {!isValid && localValue.length > 4 && (
              <span className="text-xs text-red-500">
                Invalid IBAN format or checksum
              </span>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>

      {/* BIC/SWIFT Input */}
      {showBIC && (
        <div className="space-y-2">
          <label htmlFor="bic" className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            BIC/SWIFT Code
            {showTooltip && (
              <div className="relative group">
                <Info className="h-3 w-3 cursor-help text-gray-500" />
                <div className="absolute left-0 top-6 z-10 hidden group-hover:block w-64 p-2 text-xs text-white bg-black rounded shadow-lg">
                  The BIC/SWIFT code identifies your bank internationally. Usually 8 or 11 characters.
                </div>
              </div>
            )}
          </label>

          <input
            id="bic"
            type="text"
            value={bicValue}
            onChange={(e) => {
              const value = e.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
              onBICChange?.(value)
            }}
            placeholder="UBSWCHZH80A"
            disabled={disabled}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              'font-mono',
              bicValue && !validateBIC(bicValue) && 'border-yellow-500',
            )}
            maxLength={11}
            autoComplete="off"
            spellCheck="false"
          />

          {bicValue && !validateBIC(bicValue) && (
            <p className="text-xs text-yellow-600">
              BIC/SWIFT code should be 8 or 11 characters
            </p>
          )}
        </div>
      )}

      {/* Helper Text */}
      {helperText && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-xs text-gray-600 dark:text-gray-400">
          {helperText}
        </div>
      )}

      {/* Default Helper Text */}
      {!helperText && (
        <div className="space-y-1 rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-xs text-gray-600 dark:text-gray-400">
          <p>• IBAN format varies by country (15-34 characters)</p>
          <p>• Spaces are added automatically for readability</p>
          <p>• Your bank details are encrypted and secure</p>
        </div>
      )}
    </div>
  )
})

IBANInput.displayName = "IBANInput"